import { watch, onBeforeUnmount, type Ref } from "vue";

const STORAGE_PREFIX = "sb:form-draft:";
const DEFAULT_DEBOUNCE_MS = 500;
const DEFAULT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredDraft {
  savedAt: number;
  data: Record<string, any>;
}

interface UseFormDraftOptions<T extends Record<string, any>> {
  /**
   * Unique key for this draft. Two forms that pass the same key (and matching
   * `serialize` shapes) share one draft — e.g. two different UIs for jotting a
   * note about the same contact.
   */
  key: string;
  /** Only these fields are persisted/restored verbatim. Omit if using `serialize`. */
  fields?: (keyof T)[];
  /**
   * Full control over what's written to storage, keyed by a shape independent of
   * `T`'s own field names — needed when multiple forms with different field names
   * (e.g. `message` vs `note`) must read/write the same underlying draft. Overrides `fields`.
   */
  serialize?: (value: T) => Record<string, any>;
  /** Debounce between edits and the localStorage write. */
  debounceMs?: number;
  /** Drafts older than this are treated as if they don't exist. */
  maxAgeMs?: number;
  /** Skip persisting when the form is effectively blank. */
  isEmpty?: (value: Record<string, any>) => boolean;
}

/**
 * Persists a form's text fields to localStorage so unsaved input survives an
 * accidental modal close/reopen. Best-effort only: storage errors are swallowed
 * since a lost draft should never break the form itself.
 */
export function useFormDraft<T extends Record<string, any>>(
  source: Ref<T>,
  options: UseFormDraftOptions<T>
) {
  const storageKey = `${STORAGE_PREFIX}${options.key}`;
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const maxAgeMs = options.maxAgeMs ?? DEFAULT_MAX_AGE_MS;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  // Tracks whether `source` has changed since the last persist/clear, so unmount
  // only flushes real pending edits instead of re-writing an already-cleared draft.
  let dirty = false;

  const serialize = (value: T): Record<string, any> => {
    if (options.serialize) return options.serialize(value);
    if (!options.fields) return value;
    const picked: Record<string, any> = {};
    for (const field of options.fields) picked[field as string] = value[field];
    return picked;
  };

  const readStored = (): StoredDraft | null => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredDraft;
      if (!parsed?.savedAt || Date.now() - parsed.savedAt > maxAgeMs) {
        localStorage.removeItem(storageKey);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  };

  const hasDraft = (): boolean => readStored() !== null;

  const loadDraft = (): Record<string, any> | null => readStored()?.data ?? null;

  const persist = (value: T) => {
    dirty = false;
    try {
      const data = serialize(value);
      if (options.isEmpty?.(data)) {
        localStorage.removeItem(storageKey);
        return;
      }
      const stored: StoredDraft = { savedAt: Date.now(), data };
      localStorage.setItem(storageKey, JSON.stringify(stored));
    } catch {
      // localStorage full/unavailable — drafts are best-effort, never fatal.
    }
  };

  const cancelPendingSave = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  };

  const clearDraft = () => {
    cancelPendingSave();
    dirty = false;
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  };

  const stopWatch = watch(
    source,
    (value) => {
      dirty = true;
      cancelPendingSave();
      debounceTimer = setTimeout(() => persist(value), debounceMs);
    },
    { deep: true }
  );

  onBeforeUnmount(() => {
    // Flush rather than discard: the component is almost always unmounting
    // because the modal is being closed, which is the exact moment we need
    // the latest edits captured, not thrown away with the pending timer.
    // Only flush if something actually changed since the last persist/clear —
    // otherwise an explicit clearDraft() (e.g. after a successful save) would
    // get silently undone by re-writing the still-populated field on unmount.
    cancelPendingSave();
    if (dirty) persist(source.value);
    stopWatch();
  });

  return {
    hasDraft,
    loadDraft,
    clearDraft,
  };
}
